import { Skeleton, cardVariants } from "@attmos/flowstack-ui";
import { Link, useParams } from "react-router-dom";

import type { ProjectRun } from "@/api/projects";
import { useProject, useProjectRuns } from "@/api/projects";
import { ApiError } from "@/api/client";
import { useRunFeed } from "@/api/runs";
import { relativeTime } from "@/components/grid/cells";
import { SettlementUnknownNotice } from "@/components/analysis/SettlementUnknownNotice";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { absentTimingLabel } from "@/components/runs/run-timing";
import { cn } from "@/lib/utils";
import type { AnalysisFlowRunEntry } from "@/routes/analysis/analysis-flow-runs";
import { analysisEntries, analysisRows } from "@/routes/analysis/analysis-flow-runs";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Analysis surface, step 2 — a project's runs that can carry an analysis
 * (flowstack VIZ U4.1). `/analysis/:slug/flow-runs/:flow_run_id` (a flow run)
 * or `/analysis/:slug/:run_id` (a standalone run) is the next step, once one
 * is picked.
 *
 * **One row per FLOW RUN, not per step (fs#1861)** — see
 * `analysis-flow-runs.ts` for the grouping and for which step a flow run's
 * analysis is read through.
 *
 * Which runs count as ANALYZABLE, and why, is `ANALYZABLE_RUN_STATES` in
 * `@/api/runs` — shared with the cross-run comparison's run picker (VIZ
 * U2.1) so the two lists cannot disagree about what is comparable.
 *
 * The two project-level empty states this unit exists to distinguish
 * (D71-iii's "the empty states are the part that will be got wrong"):
 * a project with NO runs at all reads differently from a project that HAS
 * runs but none of them are analyzable yet — the first says "nothing has
 * been submitted here", the second says "something is in flight (or ended
 * without science to show), come back later or check status". Conflating
 * them into one "nothing to show" screen would lose exactly the
 * distinction the operator asked for.
 *
 * The other two empty states D71(iii) names — a report not generated yet,
 * and a report that failed to load — belong to a SPECIFIC run once picked,
 * not to this list; `AnalysisRunView` covers those by rendering the
 * existing `AnalysisTab` unchanged (reuse, not fork).
 */
export function AnalysisProjectRunsPage(): JSX.Element {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  const runs = useProjectRuns(slug);
  // Whether a flow run has settled is its rollup's call, not the window of
  // steps the project feed returned (Rupert PR-1865 r1) — see
  // `flowRunSettlement`. Not essential to the list (r2): without it, flow runs
  // are listed with their status unknown, and nothing is generated for them.
  const feed = useRunFeed();

  if (project.isLoading || runs.isLoading || feed.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-1/4" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "The FlowStack server returned an error."
        }
      />
    );
  }

  if (runs.isError) {
    return (
      <EmptyState
        title="Could not load this project's runs."
        description={runs.error instanceof Error ? runs.error.message : "Unknown error."}
      />
    );
  }

  // Rupert PR-935 r0: a query that is neither loading nor errored but has
  // no data yet (a real, if narrow, TanStack Query state) must not render a
  // silent blank page — the worst of the states this surface exists to
  // keep distinct.
  if (!project.data) {
    return (
      <EmptyState
        title="Could not load project."
        description="The project query returned no data. Try again, or return to Analysis."
        cta={
          <Link to="/analysis" className="text-sm font-medium text-cyan hover:underline">
            ← Back to Analysis
          </Link>
        }
      />
    );
  }

  const breadcrumbs = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Link to="/analysis" className="hover:text-foreground">
        Analysis
      </Link>
      <span aria-hidden>·</span>
      <span className="font-mono">{project.data.slug}</span>
    </div>
  );

  const allRuns = analysisRows(feed.data?.runs ?? [], runs.data ?? [], slug);
  const { entries, activeCount, unanalyzableCount } = analysisEntries(
    allRuns,
    feed.data?.flowRuns ?? []
  );
  const slugPath = project.data.slug;
  const anyUnknown = entries.some((e) => e.kind === "flow" && e.settlement === "unknown");

  return (
    <div className="flex flex-col gap-6">
      {breadcrumbs}
      {anyUnknown && <SettlementUnknownNotice feedFailed={feed.isError} scope="list" />}
      <PageHeader
        title={project.data.display_name}
        description="Flow runs whose results can be analysed."
        actions={
          // VIZ U2.1: this list is what makes "compare these two" a coherent
          // action rather than a URL someone hand-assembles, so the entry
          // point belongs here. Offered only when there is more than one run
          // to compare — a single-run project has nothing to overlay.
          entries.length > 1 ? (
            <Link
              to={`/analysis/${project.data.slug}/compare`}
              className="text-sm font-medium text-cyan hover:underline"
            >
              Compare runs →
            </Link>
          ) : undefined
        }
      />

      {allRuns.length === 0 ? (
        <EmptyState
          title="No runs yet."
          description="This project has no runs to analyse. Submit a Flow from the project page first."
          cta={
            <Link
              to={`/projects/${project.data.slug}`}
              className="text-sm font-medium text-cyan hover:underline"
            >
              Go to project →
            </Link>
          }
        />
      ) : entries.length === 0 ? (
        <EmptyState
          title="No analyzable runs yet."
          description={`${[
            activeCount > 0 ? `${activeCount} running` : null,
            unanalyzableCount > 0 ? `${unanalyzableCount} failed before publishing results` : null,
          ]
            .filter(Boolean)
            .join(", ")} — none has published results an analysis can fit against yet.`}
          cta={
            <Link
              to={`/projects/${project.data.slug}`}
              className="text-sm font-medium text-cyan hover:underline"
            >
              View run status →
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.map((entry) => (
            <li key={entry.kind === "flow" ? entry.flowRunId : entry.run.run_id}>
              <Link
                to={
                  entry.kind === "flow"
                    ? `/analysis/${slugPath}/flow-runs/${encodeURIComponent(entry.flowRunId)}`
                    : `/analysis/${slugPath}/${entry.run.run_id}`
                }
                // Card-shaped, but a link — recipe as a class string so the
                // element keeps its own focus ring. See `cardVariants`.
                //
                // The inset is BOTH axes off the scale (flowstack#1054), which
                // is the answer to the question that issue left open: this is a
                // card that is also a row, so it takes the card's content inset
                // horizontally (`px-card`) and the row rhythm vertically
                // (`py-row-y`). Not `p-card` in both, which would make a list
                // row as tall as a content card; not `px-gutter`, which is the
                // page's inset and has no business inside a card.
                //
                // At VIS/V0b's values that is 16px/8px against the 16px/12px it
                // replaces: horizontally identical, and 4px tighter vertically
                // — which is the point, since 18px (`text-sm`) + 2 * 8px is the
                // 34px `--ag-row-height` every other run list in the app sits
                // on. It was the only row that did not.
                className={cn(
                  cardVariants(),
                  "flex items-center justify-between gap-3 px-card py-row-y outline-none transition-colors hover:border-cyan/40 hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
              >
                {entry.kind === "flow" ? (
                  <FlowRunRowBody entry={entry} />
                ) : (
                  <RunRowBody run={entry.run} />
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One flow run (fs#1861): the flow's name and the flow run's id, never a
 * step's. Which step the analysis is read through is the page's business
 * (`analysisStepOf`), not something the user is asked to pick.
 */
function FlowRunRowBody({ entry }: { entry: AnalysisFlowRunEntry }): JSX.Element {
  const { stepCount } = entry;
  // Without a rollup the counts are the window's, and say so (fs#1870), the
  // way the finish time beside them does.
  const recent = entry.countsFrom === "window" ? "recent " : "";
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-sm text-foreground">{entry.flowName ?? "Flow run"}</span>
        <span className="font-mono text-xs text-muted-foreground">{entry.flowRunId}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {entry.settlement === "unknown" && <span>Status unknown</span>}
        <span>
          {entry.finishedAt
            ? `${entry.finishedAtFrom === "latest-step" ? "Latest step " : ""}${relativeTime(new Date(entry.finishedAt))}`
            : absentTimingLabel(entry.step.status)}
        </span>
        <span>
          {stepCount} {recent}
          {stepCount === 1 ? "step" : "steps"}
          {entry.failedSteps > 0 && (
            <span className="text-destructive"> · {entry.failedSteps} failed</span>
          )}
        </span>
      </div>
    </>
  );
}

function RunRowBody({ run }: { run: ProjectRun }): JSX.Element {
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-sm text-foreground">
          {run.flow_name ?? run.tile_name ?? run.run_id}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{run.run_id}</span>
      </div>
      <div className="flex items-center gap-3">
        {/* flowstack#1270: `finished_at` is real now, and still
            null for a run VCM never timed. A terminal row says
            "not recorded" rather than showing a dash a reader
            would file under "nothing happened". */}
        <span className="text-xs text-muted-foreground">
          {run.finished_at
            ? relativeTime(new Date(run.finished_at))
            : absentTimingLabel(run.status)}
        </span>
        <RunStateBadge state={run.status} />
      </div>
    </>
  );
}
